import React, { useEffect, useState, useMemo } from "react";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
    useGetAllNotificationTemplatesAdminMutation,
    useUpdateNotificationTemplateStatusAdminMutation,
} from "../../../features/admin/notificationTemplate/NotificationTemplateApiAdmin";
import { AiFillEye } from "react-icons/ai";
import { useFetchVendorsQuery } from "../../../features/admin/vendor/vendorApi";

export default function AdminNotificationTemplate() {
    const navigate = useNavigate();
    const [tableDataItems, setTableDataItems] = useState([]);
    const [showImageModal, setShowImageModal] = useState(null);
    const [vendorsList, setVendorsList] = useState([]);

    const [
        getAllNotificationTemplates,
        {
            data: notificationTemplatesData,
            isLoading: notificationTemplatesIsLoading,
            error: notificationTemplatesError,
        },
    ] = useGetAllNotificationTemplatesAdminMutation();

    const [
        updateStatus,
        {
            data: updateStatusData,
            error: updateStatusError,
        },
    ] = useUpdateNotificationTemplateStatusAdminMutation();

    const {
        data: vendorsData,
    } = useFetchVendorsQuery(sessionStorage.getItem("access_token")) || {};

    useEffect(() => {
        if (vendorsData && vendorsData.body) {
            setVendorsList(vendorsData.body);
        }
    }, [vendorsData]);

    useMemo(() => {
        if (notificationTemplatesData && notificationTemplatesData.body && notificationTemplatesData.body.data) {
            const arr = [...notificationTemplatesData.body.data];
            arr.sort((a, b) => a.id - b.id);
            setTableDataItems(arr);
        } else {
            setTableDataItems([]);
        }

        if (notificationTemplatesError) {
            toast.error("Something went wrong", {
                id: "notificationTemplatesError",
            });
        }
    }, [notificationTemplatesData, notificationTemplatesError]);

    useEffect(() => {
        getAllNotificationTemplates({
            token: sessionStorage.getItem("access_token"),
            body: {
                limit: 1000,
                offset: 0,
            },
        });
    }, []);

    useEffect(() => {
        if (updateStatusData) {
            toast.success("Status Updated", {
                id: "updateStatusSuccess",
            });
            getAllNotificationTemplates({
                token: sessionStorage.getItem("access_token"),
                body: {
                    limit: 1000,
                    offset: 0,
                },
            });
        }

        if (updateStatusError) {
            toast.error("Something went wrong", {
                id: "updateStatusError",
            });
        }
    }, [updateStatusData, updateStatusError]);

    const handleStatusChange = (row) => {
        const newStatus = row.status === "1" ? "0" : "1";
        updateStatus({
            token: sessionStorage.getItem("access_token"),
            id: row.id,
            status: newStatus,
        });
    };

    const columns = [
        {
            name: "S.No",
            selector: (row, idx) => idx + 1,
            sortable: true,
            width: "80px",
        },
        {
            name: "Notification Type",
            selector: (row) => row.notification_type,
            sortable: true,
        },
        {
            name: "Title",
            selector: (row) => row.title,
            sortable: true,
        },
        {
            name: "Body",
            selector: (row) => row.body,
            sortable: true,
        },
        {
            name: "Tenant Name",
            cell: (row) => {
                const vendor = vendorsList.find(v => v.tenant_id == row.tenant_id);
                return vendor ? vendor.name.charAt(0).toUpperCase() + vendor.name.slice(1) : "-";
            },
            sortable: true,
        },
        {
            name: "Image",
            cell: (row) =>
                row.image && row.image.trim() !== "" ? (
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-info"
                        onClick={() => setShowImageModal(row.image)}
                    >
                        <AiFillEye />
                    </button>
                ) : (
                    <span className="text-muted">-</span>
                ),
            sortable: false,
            width: "100px",
        },
        {
            name: "Status",
            cell: (row) =>
                row.status === "1" ? (
                    <button
                        className="btn btn-outline-success btn-sm"
                        onClick={() => handleStatusChange(row)}
                    >
                        Active
                    </button>
                ) : (
                    <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => handleStatusChange(row)}
                    >
                        InActive
                    </button>
                ),
            sortable: true,
            width: "120px",
        },
        {
            name: "Actions",
            cell: (row) => (
                <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => navigate("edit", { state: row })}
                >
                    Edit
                </button>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        },
    ];

    if (notificationTemplatesIsLoading) {
        return <div>Loading...</div>;
    }

    return (
        <div className="">
            <div className="page-content">
                <div className="card p-4">
                    <div className="borderbtm d-flex justify-content-between align-items-center flex-wrap grid-margin">
                        <div>
                            <h4 className="mb-0 card-title">Notification Templates</h4>
                        </div>
                        <div className="d-flex align-items-center flex-wrap text-nowrap">
                            <button
                                type="button"
                                className="btn btn-outline-primary btn-icon-text mb-2 mb-md-0"
                                onClick={() => navigate("add")}
                            >
                                Add Notification Template
                            </button>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-12 col-xl-12">
                            <DataTable
                                title="Notification Templates"
                                data={tableDataItems}
                                columns={columns}
                            />
                        </div>
                    </div>

                    {/* Image Modal */}
                    {showImageModal && (
                        <div
                            className="modal fade show"
                            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
                            onClick={() => setShowImageModal(null)}
                        >
                            <div className="modal-dialog modal-dialog-centered">
                                <div className="modal-content">
                                    <div className="modal-header">
                                        <h5 className="modal-title">Notification Image</h5>
                                        <button
                                            type="button"
                                            className="btn-close"
                                            onClick={() => setShowImageModal(null)}
                                        ></button>
                                    </div>
                                    <div className="modal-body">
                                        <img
                                            src={showImageModal}
                                            alt="Notification"
                                            className="img-fluid"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
